// filepath: /home/vosvald/sites/wishmaker-user-area-react/src/pages/mobile-signature/mobile-signature.jsx
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from 'styled-components';
import SignaturePad from 'signature_pad';
import { FormattedMessage } from 'react-intl';
import Button from '../../components/ui/button';
import InlineLoading from '../../components/ui/inline-loading';
import { publicAreaApi } from '../../utils/api';
import { ReactComponent as WishmakerLogo } from '../../assets/img/svg/wishmaker-logo-big.svg';

const Page = styled.div`
  height: 100svh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background: #3A3851;
  box-sizing: border-box;
  overflow: hidden;
  overscroll-behavior: contain;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #3A3851;
  color: #fff;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  svg { height: 24px; width: auto; }
  svg, svg * { fill: #ffffff !important; }
  .title { font-weight: 700; font-size: 1.1rem; margin-left: auto; text-align: right; }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100vw;
  box-sizing: border-box;
  overflow: hidden;
`;

const SignatureArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100vw;
  max-width: 100vw;
  gap: 2vw;
  padding-left: 5vw;
  padding-right: 5vw;
  box-sizing: border-box;
  @media (max-width: 600px) {
    gap: 2vw;
    padding-left: 3vw;
    padding-right: 3vw;
  }
`;

const CanvasWrap = styled.div`
  flex: 1 1 0;
  aspect-ratio: 18/6;
  max-width: 80vw;
  margin-right: 2vw;
  background: transparent;
  border-radius: 16px;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 16px;
  height: 100%;
  min-width: 90px;
  max-width: 120px;
  margin-right: 2vw;
  @media (max-width: 600px) {
    min-width: 70px;
    max-width: 90px;
    gap: 12px;
    margin-right: 1vw;
  }
  button {
    width: 100%;
    font-size: 1rem;
    padding: 0.7em 0.5em;
    border-radius: 12px;
    height: 2.7em;
    max-height: 44px;
  }
`;

const Status = styled.div`
  text-align: center;
  font-size: 1.1rem;
  color: #fff;
  padding: 2vh 4vw;
`;

const MobileSignaturePage = ({ location }) => {
  const canvasRef = useRef();
  const sigPadRef = useRef();
  const contentRef = useRef();
  const actionsRef = useRef();
  const statusRef = useRef();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [canvasBox, setCanvasBox] = useState({ width: 0, height: 0 });
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  const token = new URLSearchParams(location.search).get('token');

  // Reusable SignaturePad options
  const padOptions = {
    penColor: '#1565c0',
    backgroundColor: '#fff',
    minWidth: 1.5,
    maxWidth: 3,
  };

  // Lock document scroll while this page is mounted
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Compute the largest canvas that fits into the available area
  const computeCanvasBox = () => {
    if (!isLandscape) return; // Zabránime výpočtu v portrait režime
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const cs = getComputedStyle(contentEl);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padBottom = parseFloat(cs.paddingBottom) || 0;
    const padLeft = parseFloat(cs.paddingLeft) || 0;
    const padRight = parseFloat(cs.paddingRight) || 0;
    const rowGap = parseFloat(cs.rowGap) || 0;

    // Inner content box (excluding padding)
    const innerWidth = Math.max(0, contentEl.clientWidth - padLeft - padRight);
    const innerHeight = Math.max(0, contentEl.clientHeight - padTop - padBottom);

    const actionsH = actionsRef.current ? actionsRef.current.offsetHeight : 0;
    const statusH = statusRef.current ? statusRef.current.offsetHeight : 0;

    // Reserve gaps between stacked sections: Canvas -> Actions, Actions -> Status
    const reservedGaps = rowGap * 2;

    const availableHeight = Math.max(0, innerHeight - actionsH - statusH - reservedGaps);

    // Enforce 3.5:1 aspect ratio (width:height = 3.5:1)
    const ASPECT = 3.5;

    let width = innerWidth;
    let height = width / ASPECT;

    if (height > availableHeight) {
      height = availableHeight;
      width = height * ASPECT;
    }

    // Ensure minimum height for usability
    height = Math.max(height, 80);
    width = Math.max(width, height * ASPECT);

    width = Math.max(0, Math.floor(width));
    height = Math.max(0, Math.floor(height));

    setCanvasBox(prev => {
      // Ak už máme väčšiu veľkosť, nezmenšuj ju
      if (width < prev.width || height < prev.height) {
        return prev;
      }
      return (prev.width !== width || prev.height !== height ? { width, height } : prev);
    });
  };

  // Only initialize and keep signature pad in sync with canvas size when in landscape
  useEffect(() => {
    if (!isLandscape || !canvasRef.current) return;

    const canvas = canvasRef.current;

    const initSigPad = () => {
      sigPadRef.current = new SignaturePad(canvas, padOptions);
      sigPadRef.current.clear();
    };

    const resizeCanvasPixels = (preserve = true) => {
      if (!isLandscape) return; // Zabránime resize v portrait režime
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const data = preserve && sigPadRef.current ? sigPadRef.current.toData() : null;
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      if (!sigPadRef.current) {
        initSigPad();
      } else {
        sigPadRef.current.off();
        sigPadRef.current = new SignaturePad(canvas, padOptions);
        if (data && data.length) {
          try { sigPadRef.current.fromData(data); } catch (e) { /* ignore */ }
        }
      }
    };

    // Initial layout then pixel resize
    const raf1 = requestAnimationFrame(() => {
      computeCanvasBox();
      const raf2 = requestAnimationFrame(() => resizeCanvasPixels(false));
      return () => cancelAnimationFrame(raf2);
    });

    const onResize = () => {
      if (!isLandscape) return; // Zabránime resize v portrait režime
      computeCanvasBox();
      requestAnimationFrame(() => resizeCanvasPixels(true));
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (sigPadRef.current) sigPadRef.current.off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandscape]);

  // Re-apply pixel size whenever our layout box changes, only in landscape
  useLayoutEffect(() => {
    if (!isLandscape) return;
    const canvas = canvasRef.current;
    if (!canvas || !canvasBox.width || !canvasBox.height) return;
    canvas.style.width = canvasBox.width + 'px';
    canvas.style.height = canvasBox.height + 'px';
    requestAnimationFrame(() => {
      if (!canvasRef.current) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvasRef.current.getBoundingClientRect();
      const data = sigPadRef.current ? sigPadRef.current.toData() : null;
      canvasRef.current.width = rect.width * ratio;
      canvasRef.current.height = rect.height * ratio;
      const ctx = canvasRef.current.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      if (!sigPadRef.current) {
        sigPadRef.current = new SignaturePad(canvasRef.current, padOptions);
      } else {
        sigPadRef.current.off();
        sigPadRef.current = new SignaturePad(canvasRef.current, padOptions);
      }
      if (data && data.length) {
        try { sigPadRef.current.fromData(data); } catch (e) { /* ignore */ }
      }
    });
  }, [canvasBox, padOptions, isLandscape]);

  useEffect(() => {
    if (!token) {
      // token is required for submit; we silently keep the UI without showing error text here
    }
  }, [token]);

  const handleClear = () => {
    if (isSubmitting) return;
    if (sigPadRef.current) sigPadRef.current.clear();
  };

  const handleSubmit = async () => {
    if (!token) {
      return;
    }
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      return;
    }
    try {
      setIsSubmitting(true);
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await publicAreaApi.submitMobileSignature({ token, signature: dataUrl });
      setSubmitOk(true);
    } catch (e) {
      // On backend error, hide canvas/actions and show error info with close action
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inline style to align actions block edges with the canvas width

  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  return (
    <Page>
      <Header>
        <WishmakerLogo />
        <div className="title">
          <FormattedMessage id="signature_modal_title" defaultMessage="Sign to confirm" />
        </div>
      </Header>
      <Content ref={contentRef}>
        {!isLandscape ? (
          <Status ref={statusRef}>
            <FormattedMessage id="mobile_signature_rotate_message" defaultMessage="Otočte zariadenie naležato pre podpis." />
          </Status>
        ) : submitOk ? (
          <Status ref={statusRef}>
            <FormattedMessage id="mobile_signature_submit_success" defaultMessage="Podpis bol odoslaný. Stránku je možné zavrieť." />
          </Status>
        ) : submitError ? (
          <Status ref={statusRef}>
            <FormattedMessage id="mobile_signature_submit_error_message" defaultMessage="Nastala chyba pri odoslaní podpisu. Stránku je možné zavrieť." />
          </Status>
        ) : (
          <SignatureArea>
            <CanvasWrap style={{ width: canvasBox.width + 'px', height: canvasBox.height + 'px' }}>
              {/* canvas will be sized via style and pixel density adjusted in effects */}
              <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', background: '#fff', borderRadius: '16px' }} />
            </CanvasWrap>
            <Actions ref={actionsRef} style={{ height: canvasBox.height + 'px' }}>
              <Button type="SECONDARY" onClick={handleClear} disabled={isSubmitting}>
                <FormattedMessage id="signature_modal_clear" defaultMessage="Clear" />
              </Button>
              <Button type="PRIMARY" onClick={handleSubmit} disabled={isSubmitting || !token}>
                {isSubmitting ? <InlineLoading /> : (
                  <FormattedMessage id="signature_modal_submit" defaultMessage="Confirm signature" />
                )}
              </Button>
            </Actions>
          </SignatureArea>
        )}
      </Content>
    </Page>
  );
};

export default MobileSignaturePage;
